import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { ChevronDownIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";

import usePartDetails from "../../../data/usePartDetails";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useOffers from "../../../data/useOffers";
import { IEbayOffer } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import LoadingPulse from "../../shared/LoadingPulse";
import Tooltip from "../../shared/Tooltip";
import EbayTitleIntelligenceModal from "./EbayTitleIntelligenceModal";
import EbayListingTitle, { computeEbayListingTitle } from "../EbayListingTitle";

const EBAY_TITLE_MAX = 80;

type Props = { part: IPart; isExpanded: boolean };

export default function UnlistedEbayTitleAboveRow({ part, isExpanded }: Props) {
  const queryClient = useQueryClient();
  const { update, createDefaultOffer } = useOffers();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Title");

  const [titleIntelligenceOpen, setTitleIntelligenceOpen] = useState(false);
  const [titleBuilderOpen, setTitleBuilderOpen] = useState(false);
  /** When the data row is expanded we show the builder unless the user hid it with the chevron. */
  const [suppressBuilderForExpandedRow, setSuppressBuilderForExpandedRow] =
    useState(false);

  useEffect(() => {
    if (!isExpanded) setSuppressBuilderForExpandedRow(false);
  }, [isExpanded]);

  const showTitleBuilder =
    titleBuilderOpen || (isExpanded && !suppressBuilderForExpandedRow);

  const toggleTitleBuilder = () => {
    if (showTitleBuilder) {
      setTitleBuilderOpen(false);
      if (isExpanded) setSuppressBuilderForExpandedRow(true);
    } else {
      setTitleBuilderOpen(true);
      setSuppressBuilderForExpandedRow(false);
    }
  };

  const { partDetails, isLoading: detailsLoading } = usePartDetails(
    part.id,
    showTitleBuilder
  );

  const fullPart = partDetails ?? part;

  const mergedTitle = useMemo(() => computeEbayListingTitle(part), [part]);

  const [lineValue, setLineValue] = useState(mergedTitle);
  const lastCommitted = useRef(mergedTitle);

  useEffect(() => {
    setLineValue(mergedTitle);
    lastCommitted.current = mergedTitle;
  }, [mergedTitle]);

  const countLen = lineValue.length;

  const saveLineTitle = useCallback(
    (raw: string) => {
      if (!part?.id) return;
      const nextTitle = raw.trim();
      if (nextTitle === lastCommitted.current.trim()) return;

      const detailsCache: any = queryClient.getQueryData([
        "partDetails",
        part.id,
      ]);
      const partCache: any = queryClient.getQueryData(["parts", part.id]);
      const detailsPart = detailsCache?.data ?? detailsCache?.data?.data;
      const singlePart = partCache?.data ?? partCache?.data?.data;
      const latestOffers =
        detailsPart?.offers ?? singlePart?.offers ?? part.offers ?? [];
      const offer = latestOffers?.[0] ?? part.offers?.[0];
      const updatedOffer: IEbayOffer = {
        ...(offer as IEbayOffer),
        title: nextTitle,
      };

      if (offer) {
        update.mutate(updatedOffer, {
          ...saveHandlers,
          onSuccess: () => {
            saveHandlers.onSuccess();
            if (part.offers?.[0]) {
              part.offers[0].title = nextTitle;
            }
            lastCommitted.current = nextTitle;
          },
        });
      } else {
        createDefaultOffer.mutate(
          {
            partId: part.id,
            body: updatedOffer as unknown as Record<string, unknown>,
          },
          {
            ...saveHandlers,
            onSuccess: (data: any) => {
              saveHandlers.onSuccess();
              const createdOffer = data?.data?.offers?.[0];
              if (createdOffer) {
                part.offers = [createdOffer];
              } else {
                part.offers = [{ ...updatedOffer }] as IPart["offers"];
              }
              lastCommitted.current = nextTitle;
            },
          }
        );
      }
    },
    [part, queryClient, update, createDefaultOffer, saveHandlers]
  );

  const onLineBlur = () => {
    saveLineTitle(lineValue);
  };

  const onLineKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const applyIntelligenceTitle = (next: string) => {
    setLineValue(next);
    saveLineTitle(next);
  };

  // Scratch form hooks for the embedded EbayListingTitle template editor. The
  // template editor (the one used on the eBay Listing / EditListing page) is
  // built against react-hook-form; we give it a throwaway form so its internal
  // wiring works, but the authoritative title/save flow still lives on the
  // compact top row.
  const { register, getValues, setValue, watch } = useForm<{ title: string }>({
    defaultValues: { title: lineValue },
  });

  return (
    <div
      className="-mx-4 -my-4 flex min-w-0 flex-col border-l-[3px] border-sky-400 bg-gradient-to-r from-sky-50/80 via-sky-50/40 to-sky-50/80 px-3 py-2 sm:-mx-5 sm:-my-5 sm:px-4"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <EbayTitleIntelligenceModal
        part={part}
        currentTitle={lineValue}
        open={titleIntelligenceOpen}
        setOpen={setTitleIntelligenceOpen}
        onApplyTitle={applyIntelligenceTitle}
      />

      <div className="flex min-w-0 items-center gap-2">
        <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wide text-sky-700 sm:inline">
          eBay Title
        </span>
        <div className="flex min-w-0 flex-1 items-stretch gap-1.5">
          <input
            type="text"
            name="unlisted-ebay-title-line"
            className="h-8 min-w-0 flex-1 rounded-md border border-sky-200 bg-white px-2.5 py-1 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-slate-100"
            value={lineValue}
            onChange={(e) => setLineValue(e.target.value)}
            onBlur={onLineBlur}
            onKeyDown={onLineKeyDown}
            maxLength={EBAY_TITLE_MAX}
            disabled={part.soldDate != null}
            title={
              part.soldDate != null
                ? "Cannot edit title for sold parts"
                : "Edit listing title — saves when you leave the field or press Enter"
            }
            autoComplete="off"
          />
          <Tooltip
            content={
              part.soldDate != null
                ? "Not available for sold parts"
                : "Title intelligence — eBay-style title ideas"
            }
          >
            <span className="inline-flex">
              <button
                type="button"
                onClick={() => setTitleIntelligenceOpen(true)}
                disabled={part.soldDate != null}
                aria-label="Title intelligence"
                className={classNames(
                  "inline-flex h-8 w-8 flex-none items-center justify-center rounded-md border shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary-500/35",
                  part.soldDate != null
                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                    : "border-violet-200 bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/60 hover:border-violet-300 hover:bg-violet-100"
                )}
              >
                <SparklesIcon className="h-4 w-4" aria-hidden />
              </button>
            </span>
          </Tooltip>
        </div>
        <span
          className={classNames(
            "shrink-0 tabular-nums text-[11px]",
            countLen > EBAY_TITLE_MAX
              ? "font-semibold text-amber-700"
              : "text-sky-700/80"
          )}
          title="Characters in the title (eBay max 80)"
        >
          {countLen}/{EBAY_TITLE_MAX}
        </span>
        <button
          type="button"
          onClick={toggleTitleBuilder}
          aria-expanded={showTitleBuilder}
          aria-label={
            showTitleBuilder
              ? "Hide title builder (merge tiles & highlights)"
              : "Show title builder (merge tiles & highlights)"
          }
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-sky-200 bg-white text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
        >
          <ChevronDownIcon
            className={classNames(
              "h-4 w-4 transition-transform",
              showTitleBuilder && "rotate-180"
            )}
            aria-hidden
          />
        </button>
      </div>

      {showTitleBuilder && (
        <div className="mt-2 border-t border-sky-200/70 pt-2">
          {detailsLoading && !partDetails ? (
            <div className="py-3">
              <LoadingPulse />
            </div>
          ) : (
            <div className="rounded-lg border border-sky-200/70 bg-white/70 p-3 shadow-sm">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                eBay listing template
              </p>
              <EbayListingTitle
                key={fullPart.id}
                part={fullPart}
                onTable
                register={register}
                setValue={setValue}
                watch={watch}
                getValues={getValues}
                editDefault
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
